/**
 * Bridge: project the docx-native editor's LIVE tracked changes into the
 * relational `document_suggestions` table.
 *
 * WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * The entire PropDocs backend counts/gates suggestions off `document_suggestions`
 * rows, NOT off the document body:
 *   - deal "send for signing" is blocked while `document_suggestions.pending`
 *     exists (prepare_to_sign_validator_service / Document#change_suggestions_complete?)
 *   - template publish is blocked while pending suggestions exist
 *     (DocumentTemplate#has_pending_suggestions?)
 *   - notification badges count these rows.
 * The CKEditor track-changes adapter kept that table in sync. The docx-native
 * editor stores tracked changes only INSIDE the .docx body (`w:ins`/`w:del`),
 * so without this bridge the table stays empty and every one of those gates is
 * blind — you can publish / send with unresolved suggestions, and counts read 0.
 *
 * WHAT IT DOES ────────────────────────────────────────────────────────────
 * On each editor change (debounced, alongside autosave) it:
 *   1. takes the doc's current tracked changes (body AND header/footer),
 *   2. maps each to a STABLE `external_id` derived from its `revisionId`,
 *   3. `createDocumentSuggestion` for ids not seen before,
 *   4. `syncDocumentSuggestions` with the full current id set — the backend
 *      marks any pending row whose id is no longer present as `deleted`
 *      (a change that was accepted/rejected in-editor disappears from the body).
 * The relational table therefore MIRRORS the live document's pending changes.
 *
 * external_id SHAPE: `docx:{documentType}:{documentId}:{revisionId}`.
 * `revisionId` (the OOXML `w:id`) is only unique WITHIN one document, but the
 * table's `external_id` is globally unique — so it must be namespaced by
 * document. The shape is deterministic, so re-deriving it on every sync gives
 * the SAME id for the same change (no duplicate rows across sessions).
 *
 * IDEMPOTENT + FAIL-SOFT: create is skipped for ids already synced this
 * session AND swallows the uniqueness error if a row already exists from a
 * prior session (the sync call then reconciles status). A failed sync never
 * blocks editing or autosave — the gate simply keeps the last known state.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation } from '@apollo/client';
import type { TrackedChangeEntry } from '@propdocs-editor/core/utils/comments';
import { mutations } from '../../../../api';
import { decodeAuthorToken } from './authorToken';
import {
  entryKind,
  externalIdMap,
  type SuggestionSyncDocumentType,
} from './docxSuggestionSync';

export type { SuggestionSyncDocumentType } from './docxSuggestionSync';

export interface DocxSuggestionSyncHandle {
  /**
   * Reconcile the relational table against the editor's current tracked
   * changes. Resolves with the number of pending suggestions now in the doc
   * (the count the send/publish gates and notification badges care about).
   *
   * Takes the ENTRY LIST rather than an `EditorState`. It used to take the body
   * view's state and call `extractTrackedChanges` itself — but header and footer
   * content live in their own ProseMirror views, so a change made in a header
   * was invisible here: it never created a row, and the reconcile step then
   * marked any row it DID have as deleted. The signing gate counted zero and let
   * a document with unresolved header changes through. `DocxEditorRef
   * .getTrackedChanges()` returns the merged body + H/F list the review sidebar
   * already renders.
   */
  sync: (entries: readonly TrackedChangeEntry[] | null) => Promise<number>;
}

/**
 * Build the sync handle for a deal document or a template. Returns a stable
 * `sync(state)` the container calls (debounced) on editor change.
 */
export function useDocxSuggestionSync(
  documentId: string | undefined,
  documentType: SuggestionSyncDocumentType,
  /**
   * external_ids that ALREADY exist in `document_suggestions` for this record
   * (from `getDocumentById.documentSuggestions`). Seeded into the session cache
   * so the sync never attempts to `createDocumentSuggestion` for a row that
   * already exists — `create` fails the global-unique `external_id` validation
   * ("External has already been taken"), which Apollo's error link logs to the
   * console on every toggle even though we catch it. Passing these makes the
   * create genuinely idempotent across sessions.
   */
  existingExternalIds?: readonly string[]
): DocxSuggestionSyncHandle {
  const [createDocumentSuggestion] = useMutation(mutations.createDocumentSuggestion);
  const [syncDocumentSuggestions] = useMutation(mutations.syncDocumentSuggestions);

  // external_ids we've already issued a create for this session — avoids a
  // redundant create round-trip on every keystroke-debounced sync, AND avoids a
  // duplicate-create error for rows that already exist in the DB.
  const createdRef = useRef<Set<string>>(new Set());

  // Seed the cache with rows that already exist (from a prior session / the
  // prod import) so we never re-attempt their create.
  useEffect(() => {
    if (existingExternalIds) {
      for (const id of existingExternalIds) createdRef.current.add(id);
    }
  }, [existingExternalIds]);

  const sync = useCallback(
    async (entries: readonly TrackedChangeEntry[] | null): Promise<number> => {
      if (!documentId) return 0;
      const changes = entries ?? [];

      // Build the id → representative-entry map for THIS document's live changes.
      const byExternalId = externalIdMap(changes, documentType, documentId);
      const currentIds = [...byExternalId.keys()];

      // 1. Create rows for ids we haven't issued a create for yet.
      const toCreate = currentIds.filter((id) => !createdRef.current.has(id));
      await Promise.all(
        toCreate.map(async (externalId) => {
          const entry = byExternalId.get(externalId)!;
          const token = decodeAuthorToken(entry.author);
          try {
            await createDocumentSuggestion({
              variables: {
                documentId,
                documentType,
                externalId,
                type: entryKind(entry.type),
                // Enough to reconstruct the change for display/threading; the
                // body remains the source of truth for the actual content.
                data: {
                  authorId: token.authorId ?? null,
                  authorName: token.display,
                  text: entry.text ?? '',
                  origin: 'docx-native',
                },
                attributes: {},
              },
            });
          } catch {
            // A row for this external_id may already exist (prior session) —
            // the unique-constraint error is expected and harmless; the sync
            // call below reconciles its status. Never block editing.
          }
          // Mark as issued regardless: a create failure is (almost always) the
          // row already existing, so re-issuing next tick is pointless.
          createdRef.current.add(externalId);
        })
      );

      // 2. Reconcile: any pending row NOT in currentIds is now resolved/removed
      //    (accepted or rejected in-editor) → backend marks it `deleted`.
      try {
        await syncDocumentSuggestions({
          variables: { documentId, documentType, externalIds: currentIds },
        });
      } catch {
        // Keep the last known state on failure; the next change retries.
      }

      // Drop ids that vanished from our session cache so a re-inserted change
      // (same revisionId reused) is re-created rather than assumed present.
      for (const id of createdRef.current) {
        if (!byExternalId.has(id)) createdRef.current.delete(id);
      }

      // The live doc's pending-change count == the number of distinct external
      // ids currently present (every one maps to a pending row after reconcile).
      return currentIds.length;
    },
    [documentId, documentType, createDocumentSuggestion, syncDocumentSuggestions]
  );

  return useMemo(() => ({ sync }), [sync]);
}
